// The ICP location vocabulary shared by every frontend editor that writes
// `company_icps.locations`. The canonical list lives in
// `apps/backend/src/constants/icpLocationTypes.js`, which the backend validator
// that gates saves and the heatmap overlay that draws stored entries both derive
// from. This package sits in the separate `apps/frontend` pnpm workspace and the
// backend's build never compiles it, so this copy cannot import that one:
// a change to the vocabulary has to be made in both.
//
// The legacy `METROREGION` spelling also occurs in stored data. The frontend
// never writes it, but reads normalize it to `METRO_REGION` so those entries
// keep their type instead of being re-inferred on save.

export const ICP_LOCATION_TYPES = ['STATE', 'ZIPCODE', 'FSA', 'METRO_REGION', 'COUNTRY'] as const;

export type IcpLocationType = (typeof ICP_LOCATION_TYPES)[number];

export interface IcpLocation {
  type: IcpLocationType;
  value: string;
}

/** Stored location values (trimmed) mapped to the type they were saved with. */
export type StoredLocationTypes = Record<string, IcpLocationType>;

// ZIP+4 is tolerated because `zipCountyMappingService.parseZipCodeString` and the
// overlay's `resolveZcta` both accept it.
const ZIP_CODE = /^\d{5}(-\d{4})?$/;

// A bare forward sortation area ("M5V") or the prefix of a full postal code
// ("M5V 3A8") — both are what the overlay's `resolveFsa` accepts.
const FSA_TOKEN = /^[A-Za-z]\d[A-Za-z]$/;
const CANADIAN_POSTAL_CODE = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;

const COUNTRY_NAMES = new Set(['united states', 'canada']);

// ZoomInfo metro display names are stored as "TX - Dallas".
const METRO_REGION_SEPARATOR = ' - ';

// Metro entries are also stored under their ZoomInfo metro id ("usa.texas.dallas"),
// which `resolveZoomInfoMetroId` accepts alongside the display name. The shape is
// matched structurally rather than against the 253-entry vocabulary, which would
// mean a fourth copy of that list in this package: every metro id is exactly three
// lowercase alphanumeric dot-separated segments, a shape no place name a user types
// can take (real names carry capitals, spaces, or fewer segments).
const METRO_REGION_ID = /^[a-z0-9]+\.[a-z0-9]+\.[a-z0-9]+$/;

// The two vocabulary entries that name a whole country's metros rather than one
// metro; they have no "XX - City" shape to recognize.
const METRO_REGION_AGGREGATES = new Set(['us metro regions', 'canadian metro regions']);

// Comma only: a full postal code carries a space that must stay with its FSA prefix.
function splitTokens(value: string): string[] {
  return value
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);
}

/**
 * A ZIPCODE entry's value is a comma-separated list as often as a single zip,
 * so a value whose every token is ZIP-shaped is a zip list however long it is.
 */
function isZipCodeValue(value: string): boolean {
  const tokens = splitTokens(value);
  return tokens.length > 0 && tokens.every((token) => ZIP_CODE.test(token));
}

/**
 * An FSA entry's value is a comma-joined set as often as a single FSA — the admin
 * target-locations modal saves a whole validated set as one value
 * (see `docs/engineering/systems/prospecting/icp-locations.md`) — so a value whose every token is
 * FSA-shaped is an FSA set however long it is.
 */
function isFsaValue(value: string): boolean {
  const tokens = splitTokens(value);
  return (
    tokens.length > 0 &&
    tokens.every((token) => FSA_TOKEN.test(token) || CANADIAN_POSTAL_CODE.test(token))
  );
}

function inferLocationType(value: string): IcpLocationType {
  if (isZipCodeValue(value)) return 'ZIPCODE';
  if (isFsaValue(value)) return 'FSA';
  if (value.includes(METRO_REGION_SEPARATOR)) return 'METRO_REGION';
  if (METRO_REGION_ID.test(value)) return 'METRO_REGION';
  if (METRO_REGION_AGGREGATES.has(value.toLowerCase())) return 'METRO_REGION';
  if (COUNTRY_NAMES.has(value.toLowerCase())) return 'COUNTRY';
  return 'STATE';
}

/**
 * The type to save a location value under.
 *
 * A value that is already stored keeps the type it was stored with — inference
 * runs only for values the user newly typed. `storedTypes` is required rather
 * than optional because a caller that skips it silently retypes every entry the
 * user did not touch, which is how comma-packed zip lists became `STATE` and
 * blanked the heatmap overlay (issue #2388). Pass `{}` only when there is
 * genuinely nothing stored to preserve.
 */
export function getLocationTypeForValue(
  value: string,
  storedTypes: StoredLocationTypes
): IcpLocationType {
  const trimmed = value.trim();
  if (!Object.hasOwn(storedTypes, trimmed)) return inferLocationType(trimmed);
  const storedType = storedTypes[trimmed];

  // One deliberate exception to "stored wins": ICPs written before #2388 was fixed
  // still hold zip values mistyped as `STATE`, and the backend validator now rejects
  // that pairing, so preserving it would make those ICPs permanently unsaveable.
  // Re-inferring a ZIP-shaped value repairs them on the next save. This stays narrow
  // on purpose — a ZIP-shaped value is the only value shape that cannot legitimately
  // belong to another type, whereas e.g. a comma-joined FSA set is a valid FSA value.
  if (storedType !== 'ZIPCODE' && isZipCodeValue(trimmed)) return inferLocationType(trimmed);

  return storedType;
}

// The backend accepts `METROREGION` alongside `METRO_REGION` and resolves both
// to the same metro boundary, so the older spelling maps onto the canonical one.
const LEGACY_TYPE_ALIASES: Record<string, IcpLocationType> = { METROREGION: 'METRO_REGION' };

/** Indexes already-stored locations for `getLocationTypeForValue`. */
export function buildStoredLocationTypes(
  locations: Array<{ type?: string | null; value?: string | null }> | null | undefined
): StoredLocationTypes {
  // Null-prototype so a location value that collides with an inherited member
  // ("constructor", "toString") is not read back as a stored type.
  const storedTypes: StoredLocationTypes = Object.create(null);
  if (!Array.isArray(locations)) return storedTypes;

  for (const location of locations) {
    const value = location?.value?.trim();
    const rawType = location?.type?.trim().toUpperCase();
    if (!value || !rawType) continue;
    const type = LEGACY_TYPE_ALIASES[rawType] ?? rawType;
    if ((ICP_LOCATION_TYPES as readonly string[]).includes(type)) {
      storedTypes[value] = type as IcpLocationType;
    }
  }
  return storedTypes;
}
